import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { DataBackupManager } from '../utils/dataBackup';
import bookmarkApi from '../services/bookmarkApi';
import '../styles/DataSyncDialog.css';

const DataSyncDialog = ({ isOpen, onClose, onSyncComplete }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [syncState, setSyncState] = useState('analyzing'); // analyzing, conflict, syncing, success, error
  const [comparison, setComparison] = useState(null);
  const [backupManager, setBackupManager] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && user?.email) {
      const manager = new DataBackupManager(user.email);
      setBackupManager(manager);
      analyzeData(manager);
    }
  }, [isOpen, user?.email]);

  const analyzeData = async (manager) => {
    try {
      setSyncState('analyzing');
      setProgress(20);

      // Load local backup
      const localData = manager.loadLocalBackup();
      setProgress(40);

      // Load backend data
      let backendBookmarks = [];
      try {
        backendBookmarks = await bookmarkApi.getAllBookmarks();
      } catch (err) {
        console.log('Backend not available, using empty array');
      }
      setProgress(60);

      // Compare data
      const comp = manager.compareData(localData.bookmarks, backendBookmarks);
      setComparison(comp);
      setProgress(100);

      // Determine next state
      if (comp.recommendation === 'IN_SYNC') {
        setSyncState('success');
      } else if (comp.dataLoss || comp.needsSync) {
        setSyncState('conflict');
      } else {
        setSyncState('success');
      }

    } catch (err) {
      console.error('Data analysis failed:', err);
      setError(err.message);
      setSyncState('error');
    }
  };

  const handleSyncToBackend = async () => {
    try {
      setSyncState('syncing');
      setProgress(0);
      
      if (!comparison?.localBookmarks) return;

      let synced = 0;
      const total = comparison.localBookmarks.length;

      for (const bookmark of comparison.localBookmarks) {
        try {
          await bookmarkApi.createBookmark({
            title: bookmark.title,
            url: bookmark.url,
            description: bookmark.description || '',
            category: bookmark.category || '',
            isFavorite: bookmark.isFavorite || false
          });
          synced++;
          setProgress((synced / total) * 100);
        } catch (err) {
          console.warn(`Failed to sync bookmark: ${bookmark.title}`, err);
        }
      }

      backupManager.updateSyncStatus('synced', { 
        syncedCount: synced,
        totalCount: total,
        timestamp: Date.now()
      });

      setSyncState('success');
      setTimeout(() => {
        onSyncComplete?.(synced);
      }, 1500);

    } catch (err) {
      console.error('Sync failed:', err);
      setError(err.message);
      setSyncState('error');
    }
  };

  const handleRestoreFromLocal = async () => {
    await handleSyncToBackend(); // Same process for full restore
  };

  const handleSkip = () => {
    backupManager?.updateSyncStatus('skipped', { timestamp: Date.now() });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sync-dialog-overlay">
      <div className="sync-dialog">
        <div className="sync-dialog-header">
          <h3>🔄 Daten-Synchronisation</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="sync-dialog-content">
          {syncState === 'analyzing' && (
            <div className="sync-analyzing">
              <div className="sync-spinner"></div>
              <h4>Analysiere Daten...</h4>
              <p>Vergleiche lokale Backups mit Backend-Daten</p>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          )}

          {syncState === 'conflict' && comparison && (
            <div className="sync-conflict">
              <div className="conflict-icon">⚠️</div>
              <h4>Datenkonflikt erkannt!</h4>
              
              <div className="data-comparison">
                <div className="comparison-item">
                  <strong>💾 Lokale Daten:</strong>
                  <span className="count">{comparison.localCount} Bookmarks</span>
                </div>
                <div className="comparison-item">
                  <strong>☁️ Backend-Daten:</strong>
                  <span className="count">{comparison.backendCount} Bookmarks</span>
                </div>
                {comparison.onlyInLocal > 0 && (
                  <div className="comparison-item highlight">
                    <strong>📤 Nur lokal vorhanden:</strong>
                    <span className="count">{comparison.onlyInLocal} Bookmarks</span>
                  </div>
                )}
              </div>

              {comparison.dataLoss && (
                <div className="data-loss-warning">
                  <h5>🚨 Datenverlust erkannt!</h5>
                  <p>Das Backend ist leer, aber lokale Backups sind vorhanden.</p>
                  <p><strong>Empfehlung:</strong> Lokale Daten wiederherstellen</p>
                </div>
              )}

              <div className="sync-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleRestoreFromLocal}
                  disabled={comparison.onlyInLocal === 0}
                >
                  📥 Lokale Daten wiederherstellen ({comparison.onlyInLocal})
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={handleSkip}
                >
                  ⏭️ Überspringen
                </button>
              </div>
            </div>
          )}

          {syncState === 'syncing' && (
            <div className="sync-syncing">
              <div className="sync-spinner"></div>
              <h4>Synchronisiere Daten...</h4>
              <p>Übertrage Bookmarks zum Backend</p>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <small>{Math.round(progress)}% abgeschlossen</small>
            </div>
          )}

          {syncState === 'success' && (
            <div className="sync-success">
              <div className="success-icon">✅</div>
              <h4>Synchronisation erfolgreich!</h4>
              {comparison && (
                <p>
                  {comparison.recommendation === 'IN_SYNC' 
                    ? 'Alle Daten sind bereits synchronisiert.'
                    : `${comparison.onlyInLocal} Bookmarks wurden wiederhergestellt.`
                  }
                </p>
              )}
              <button 
                className="btn btn-primary"
                onClick={() => onSyncComplete?.()}
              >
                Fertig
              </button>
            </div>
          )}

          {syncState === 'error' && (
            <div className="sync-error">
              <div className="error-icon">❌</div>
              <h4>Synchronisation fehlgeschlagen</h4>
              <p>{error}</p>
              <div className="error-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => analyzeData(backupManager)}
                >
                  Erneut versuchen
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={onClose}
                >
                  Schließen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSyncDialog;